import { theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { InputConsumer } from '../chat/InputContext';
import { ChipsContainer, Item, Remove } from '../chat/InputForm';
import { ContactSource, ContactType } from '../generated/hooks';
import { organizationRespondent } from '../types';

const Wrap = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding-left: 12px;
  padding-right: 12px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const PersonItem = styled.a`
  font-size: 14px;
  padding: 4px 0;
  border-radius: 10px;
  cursor: pointer;
  outline: 0;

  &:hover {
    strong {
      text-decoration: underline;
    }
  }
  &:selected {
    strong {
      font-weight: 700;
    }
  }

  strong {
    font-weight: 500;
    color: ${theme.colors.actionPrimary.default};
  }

  i {
    font-style: normal;
    font-weight: 400;
    color: #a1a1a1;
  }
`;

const FloatWindow = styled.div`
  position: absolute;
  left: 12px;
  right: 45px;
  max-width: 500px;
  bottom: 8px;
  height: auto;
  max-height: 250px;
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  flex-direction: column;
  background-color: #fff;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 16px 24px;
  z-index: 100;
  box-shadow: 0 10px 14px rgba(0, 0, 0, 0.01);
`;

interface IProps extends WithTranslation {
  items: organizationRespondent[];
  input: string;
  onSelect: (person: organizationRespondent) => void;
  selected: organizationRespondent[];
  handleRemoveRespondent: (respondent: organizationRespondent) => void;
  scrollToBottom: () => void;
  lastKey: string | undefined;
  setLastKey: (key: string | undefined) => void;
  disabled?: boolean;
}

interface IState {
  persons: organizationRespondent[];
  index: number;
}

const getRawContact = (
  member: organizationRespondent,
  contactType: ContactType,
  contactSource?: ContactSource,
): NonNullable<organizationRespondent['contacts']>[0] | null => {
  if (member?.contacts != null) {
    const contact = member.contacts.find(
      (contact) => contact.type === contactType && (!contactSource || contact.source === contactSource),
    );
    if (contact) {
      return contact;
    }
  }
  return null;
};

const getContact = (member: organizationRespondent, contactType: ContactType, contactSource?: ContactSource) => {
  const contact = getRawContact(member, contactType, contactSource);
  return contact ? contact.value : '';
};

// Normalize string - lower case, remove diacritics
const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

class PersonAutocomplete extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      persons: [],
      index: 0,
    };
  }

  componentDidUpdate(prevProps: IProps) {
    if (prevProps.lastKey !== this.props.lastKey) {
      const matches = this.getMatches();

      if (matches.length > 0) {
        if (this.props.lastKey === 'ArrowUp') {
          this.setState((oldState) => ({
            index: (oldState.index - 1 + matches.length) % matches.length,
          }));
        } else if (this.props.lastKey === 'ArrowDown') {
          this.setState((oldState) => ({
            index: (oldState.index + 1) % matches.length,
          }));
        } else if (this.props.lastKey === 'Enter' || this.props.lastKey === 'Tab') {
          if (matches.length > this.state.index) {
            this.onSelect(matches[this.state.index])();
          }
        }
      }
      this.props.setLastKey(undefined);
    }
  }

  onSelect = (respondent: organizationRespondent) => () => {
    this.props.onSelect(respondent);
    this.props.scrollToBottom();
    this.setState({ index: 0 });
  };

  handleRemoveRespondent = (respondent: organizationRespondent) => () => {
    this.props.handleRemoveRespondent(respondent);
  };

  renderPersons = () => {
    const { selected, disabled } = this.props;

    return (
      <ChipsContainer style={{ alignSelf: 'flex-start', marginTop: theme.spacing.f }}>
        {selected.map((item) => (
          <Item
            key={item.id}
            onClick={this.handleRemoveRespondent(item)}
            style={{ pointerEvents: disabled ? 'none' : 'auto' }}
          >
            {item.firstname + ' ' + item.surname + ' '}(
            {getContact(item, ContactType.Email, ContactSource.Primary) ||
              getContact(item, ContactType.Email, ContactSource.Respondent) ||
              getContact(item, ContactType.Sms, ContactSource.Respondent)}
            )<Remove>&times;</Remove>
          </Item>
        ))}
      </ChipsContainer>
    );
  };

  getMatches = () => {
    const { input, items, selected } = this.props;

    if (!input || input.length === 0) {
      return [];
    }

    // Normalize input for comparison
    const normalizedInput = normalizeString(input);

    // Filter out already selected items
    const selectedIds = new Set(selected.map((s) => s.id));
    const filteredItems = items.filter((item) => !selectedIds.has(item.id));

    // Group results into categories
    const firstNameStartsWith: organizationRespondent[] = [];
    const surnameStartsWith: organizationRespondent[] = [];
    const contactStartsWith: organizationRespondent[] = [];
    const containsAnywhere: organizationRespondent[] = [];

    filteredItems.forEach((person) => {
      const firstName = normalizeString(person.firstname || '');
      const surname = normalizeString(person.surname || '');

      // Check firstname starts with input
      if (firstName.startsWith(normalizedInput)) {
        firstNameStartsWith.push(person);
        return;
      }

      // Check surname starts with input
      if (surname.startsWith(normalizedInput)) {
        surnameStartsWith.push(person);
        return;
      }

      // Check contacts start with input
      const hasContactStartingWith = person.contacts?.some((contact) =>
        normalizeString(contact.value || '').startsWith(normalizedInput),
      );
      if (hasContactStartingWith) {
        contactStartsWith.push(person);
        return;
      }

      // Check if any field 'contains' the input anywhere
      const firstNameContains = firstName.includes(normalizedInput);
      const surnameContains = surname.includes(normalizedInput);
      const contactContains = person.contacts?.some((contact) =>
        normalizeString(contact.value || '').includes(normalizedInput),
      );

      if (firstNameContains || surnameContains || contactContains) {
        containsAnywhere.push(person);
      }
    });

    // Enhanced sorting: first by firstName, then by surname
    const sortByNameAndSurname = (a: organizationRespondent, b: organizationRespondent) => {
      const firstNameCompare = (a.firstname || '').localeCompare(b.firstname || '');
      // If firstNames are not the same, sort by firstName
      if (firstNameCompare !== 0) {
        return firstNameCompare;
      }
      // If firstNames are the same, sort by surname
      return (a.surname || '').localeCompare(b.surname || '');
    };

    firstNameStartsWith.sort(sortByNameAndSurname);
    surnameStartsWith.sort(sortByNameAndSurname);
    contactStartsWith.sort(sortByNameAndSurname);
    containsAnywhere.sort(sortByNameAndSurname);

    // Change the order to change the priority of the results
    return [...firstNameStartsWith, ...surnameStartsWith, ...contactStartsWith, ...containsAnywhere];
  };

  render() {
    const matches = this.getMatches();

    return (
      <Wrap>
        {this.props.selected && this.renderPersons()}
        {matches.length > 0 && (
          <FloatWindow data-cy="chat-input-options">
            {matches.map((result, index) => (
              <PersonItem
                key={result.id}
                tabIndex={index + 1}
                style={{
                  fontWeight: this.state.index === index ? 'bold' : 'normal',
                }}
                onClick={this.onSelect(result)}
              >
                <strong>
                  {result.firstname} {result.surname}
                </strong>{' '}
                <i>
                  (
                  {getContact(result, ContactType.Email, ContactSource.Primary) ||
                    getContact(result, ContactType.Email, ContactSource.Respondent) ||
                    getContact(result, ContactType.Sms, ContactSource.Respondent)}
                  )
                </i>
              </PersonItem>
            ))}
          </FloatWindow>
        )}
      </Wrap>
    );
  }
}

const GetPropsFromProvider = ({
  scrollToBottom,
  items,
  t,
  tReady,
  i18n,
  disabled,
}: Omit<IProps, 'input' | 'onSelect' | 'selected' | 'handleRemoveRespondent' | 'lastKey' | 'setLastKey'>) => (
  <InputConsumer>
    {(context) => (
      <PersonAutocomplete
        scrollToBottom={scrollToBottom}
        items={items}
        t={t}
        tReady={tReady}
        disabled={disabled}
        i18n={i18n}
        input={context.state.message}
        onSelect={context.handleSelectPerson}
        selected={context.state.selectedPersons}
        handleRemoveRespondent={context.handleRemoveRespondent}
        lastKey={context.state.lastKey}
        setLastKey={context.setLastKey}
      />
    )}
  </InputConsumer>
);

export default withTranslation('personMultiselect')(GetPropsFromProvider);
